import React, { useMemo } from "react";
import { Box, Dialog } from "@mui/material";
import { MRT_ColumnFiltersState } from "material-react-table";
import { AdaptedCollectorsMissingDocuments } from "api";
import {
  BulkActionsToolbar,
  CustomMaterialTable,
  Loading,
  Pagination,
} from "components";
import { BulkActionsButtons } from "../bulkActionsButtons";
import { CollectorMonthView } from "../collectorMonthView";
import {
  TABLE_BODY_CELL_PROPS,
  TABLE_CONTAINER_SX,
  TABLE_HEAD_CELL_PROPS,
  TABLE_LOADING_SX,
  TABLE_WRAPPER_SX,
} from "../../consts";

interface MissingDocumentsTableProps {
  loadingMissingDocuments: boolean;
  isTableRequestInProgress: boolean;
  columns: any[];
  currentMissingData: any[];
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: React.Dispatch<
    React.SetStateAction<MRT_ColumnFiltersState>
  >;
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  companyMonths: string[];
  totalRows: number;
  size: number;
  page: number;
  uiPage: number;
  handlePageChange: (page: number) => void;
  handleSizeChange: (size: number) => void;
  totalSelectedRows: number;
  allRowsSelected: boolean;
  selectedRows: Record<string, any>;
  allCurrentCollectorsIds: string[];
  fromDate: string;
  toDate: string;
  handleUpdateMissingDocuments: (payload?: any) => Promise<void>;
  openMonthView: boolean;
  setOpenMonthView: (open: boolean) => void;
  selectedMonthCell: { month: string; year: number };
  collectorMissingDocuments: AdaptedCollectorsMissingDocuments | undefined;
  allMonths: string[];
}

export const MissingDocumentsTable = ({
  loadingMissingDocuments,
  isTableRequestInProgress,
  columns,
  currentMissingData,
  columnFilters,
  setColumnFilters,
  globalFilter,
  setGlobalFilter,
  companyMonths,
  totalRows,
  size,
  page,
  uiPage,
  handlePageChange,
  handleSizeChange,
  totalSelectedRows,
  allRowsSelected,
  selectedRows,
  allCurrentCollectorsIds,
  fromDate,
  toDate,
  handleUpdateMissingDocuments,
  openMonthView,
  setOpenMonthView,
  selectedMonthCell,
  collectorMissingDocuments,
  allMonths,
}: MissingDocumentsTableProps) => {
  const selectedRowsData = useMemo(() => {
    if (allRowsSelected) return currentMissingData;
    if (Object.keys(selectedRows).length === 0) return [];
    const selectedIds = new Set(Object.keys(selectedRows));
    return currentMissingData.filter((row) => selectedIds.has(String(row.id)));
  }, [allRowsSelected, currentMissingData, selectedRows]);

  return (
    <>
      <Box sx={TABLE_WRAPPER_SX}>
        {loadingMissingDocuments || isTableRequestInProgress ? (
          <Box sx={TABLE_LOADING_SX}>
            <Loading />
          </Box>
        ) : (
          <>
            {totalSelectedRows > 0 && (
              <BulkActionsToolbar
                totalSelectedRows={totalSelectedRows}
                totalRows={totalRows}
                bulkActionsButtons={
                  <BulkActionsButtons
                    selectedCollectorIds={
                      allRowsSelected
                        ? allCurrentCollectorsIds
                        : Object.keys(selectedRows)
                    }
                    fromDate={fromDate}
                    toDate={toDate}
                    onRefreshAfterSave={handleUpdateMissingDocuments}
                    totalSelectedRows={totalSelectedRows}
                    selectedRowsData={selectedRowsData}
                  />
                }
              />
            )}
            <CustomMaterialTable
              containerSx={TABLE_CONTAINER_SX}
              muiTableHeadCellProps={TABLE_HEAD_CELL_PROPS}
              muiTableBodyCellProps={TABLE_BODY_CELL_PROPS}
              key={companyMonths as any}
              columns={columns}
              data={currentMissingData}
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              enableGlobalFilter={false}
              enablePagination
              manualPagination
              rowCount={totalRows}
              enableColumnResizing
              enablePinning
              enableStickyHeader
              muiTablePaginationProps={{
                rowsPerPageOptions: [50, 100, 200],
                showFirstButton: true,
                showLastButton: true,
              }}
              state={{
                columnFilters,
                globalFilter,
              }}
              getRowId={(row: any) => row.id}
              initialState={{
                pagination: { pageSize: size, pageIndex: page },
                columnPinning: {
                  left: [
                    "custom-row-selection-column",
                    "collector.siteName",
                    "collector.providerName",
                    "collector.accountNumber",
                    "collector.meterNumber",
                    "status",
                    "collector.utilityTypeName",
                  ],
                },
              }}
            />
          </>
        )}
      </Box>
      {!loadingMissingDocuments && !isTableRequestInProgress && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          <Pagination
            page={uiPage}
            rowsCount={totalRows}
            rowsPerPage={size}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleSizeChange}
          />
        </Box>
      )}
      {openMonthView && (
        <div>
          <Dialog
            open
            sx={{
              "& > div > div": {
                width: "1480px",
                minWidth: "1480px",
                maxWidth: "1480px",
                height: "fit-content",
                maxHeight: "90%",
                marginLeft: "130px",
                borderRadius: "8px",
                "& > div": {
                  minHeight: "100%",
                  "& > div": {
                    padding: "0",
                    height: "100%",
                  },
                },
              },
            }}
          >
            <Box>
              <CollectorMonthView
                year={selectedMonthCell.year}
                month={(
                  allMonths.indexOf(selectedMonthCell.month) + 1
                ).toString()}
                collectorMissingDocuments={collectorMissingDocuments!}
                onClose={() => setOpenMonthView(false)}
              />
            </Box>
          </Dialog>
        </div>
      )}
    </>
  );
};
